import React, { useState } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import axios from "axios";
import { TICKET_ROUTES } from "./consts";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  X,
  LogOut,
  ScanLine,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";

const Tickets = ({ onLogout }) => {
  const navigate = useNavigate();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentScanCode, setCurrentScanCode] = useState(null);
  const [scanCooldown, setScanCooldown] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const extractCode = (result) => {
    if (!result) return null;
    if (Array.isArray(result)) {
      const first = result[0];
      return first?.rawValue || first?.text || first;
    }
    return result?.rawValue || result?.text || result;
  };

  const fetchTicketInfo = async (code) => {
    setCurrentScanCode(code);
    setProcessing(true);
    setMessage(null);
    setShowButtons(false);
    // setIsCameraOpen(false); // handleScan içinde zaten kapatıyoruz

    try {
      const response = await axios.get(TICKET_ROUTES.DETAIL(code));
      const data = response.data;

      setTicketData(data);

      if (data?.is_entry) {
        setMessage({
          text: "Bu bilet zaten kullanılmış.",
          color: "text-red-600",
          icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
        });
        setShowButtons(false);
      } else if (data?.has_cancel) {
        setMessage({
          text: `İptal Tarihi: ${data.cancel_time || ""}`,
          color: "text-red-600",
          icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
        });
        setShowButtons(false);
      } else {
        setShowButtons(true);
        setMessage(null);
      }

      setModalOpen(true);
    } catch (error) {
      console.error(error);
      setTicketData(null);
      setShowButtons(false);
      setModalOpen(true);
      const msg = error.response?.data?.msg || "Bilet bilgileri alınamadı.";
      setMessage({
        text: msg,
        color: "text-red-600",
        icon: <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
      });
    } finally {
      setProcessing(false);
      setIsScanning(false); // Loading bitti
    }
  };

  const handleScan = (result) => {
    const code = result?.[0]?.rawValue || result?.[0]?.text;

    if (!code || processing || scanCooldown || modalOpen || isScanning) return;

    setScanCooldown(true);
    setIsScanning(true); // Loading başladı
    setIsCameraOpen(false); // Kamerayı geçici kapat

    fetchTicketInfo(code);

    setTimeout(() => {
      setScanCooldown(false);
    }, 1000);
  };

  const handleError = () => {
    setMessage({
      text: "Kamera erişim hatası. Lütfen tarayıcı izinlerini kontrol edin.",
      color: "text-red-600",
      icon: <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
    });
  };

  const startScanner = () => {
    setMessage(null);
    setIsCameraOpen(true);
  };

  const stopScanner = () => {
    setIsCameraOpen(false);
  };

  React.useEffect(() => {
    setIsCameraOpen(true);
  }, []);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate("/login", { replace: true });
  };

  const closeModal = () => {
    setModalOpen(false);
    setTicketData(null);
    setMessage(null);
    setShowButtons(false);
    setProcessing(false);
    setCurrentScanCode(null);
    setIsCameraOpen(true); // Modal kapanınca kamerayı tekrar aç
  };

  const cancelTicket = async () => {
    if (!currentScanCode) {
      const msg = "Bilet kodu bulunamadı!";
      alert(msg);
      return;
    }

    setProcessing(true);
    setMessage(null);

    const koltukId = ticketData?.koltuk_id || 0;

    console.log("İptal isteği atılıyor:", {
      code: currentScanCode,
      koltuk: koltukId,
      url: TICKET_ROUTES.CLOSE(currentScanCode, koltukId),
    });

    try {
      const response = await axios.get(
        TICKET_ROUTES.CLOSE(currentScanCode, koltukId)
      );
      const resCode = response.data?.code;
      const msg = response.data?.msg || "İşlem tamamlandı.";

      if (resCode === 1) {
        setMessage({
          text: msg,
          color: "text-orange-600",
          icon: (
            <AlertCircle className="w-12 h-12 text-orange-600 mx-auto mb-2" />
          ),
        });
        setShowButtons(false);
      } else {
        alert(`Hata Kodu: ${resCode}\nMesaj: ${msg}`);
        setMessage({
          text: msg,
          color: "text-red-600",
          icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
        });
      }
    } catch (error) {
      console.error("İptal Hatası:", error);
      const msg =
        error.response?.data?.msg ||
        error.message ||
        "Bilet iptal edilirken bir hata oluştu.";
      alert(`İptal İşlemi Başarısız!\n\n${msg}`);
      setMessage({
        text: msg,
        color: "text-red-600",
        icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
      });
    } finally {
      setProcessing(false);
    }
  };

  const confirmTicket = async () => {
    if (!currentScanCode) return;
    setProcessing(true);
    setMessage(null);
    try {
      const response = await axios.get(TICKET_ROUTES.ENTRY(currentScanCode));
      const resCode = response.data?.code;
      const msg = response.data?.msg || "İşlem tamamlandı.";

      if (resCode === 1) {
        setMessage({
          text: msg,
          color: "text-green-600",
          icon: (
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
          ),
        });
        setShowButtons(false);
      } else if (resCode === 3) {
        setMessage({
          text: msg,
          color: "text-red-600",
          icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
        });
        setShowButtons(false);
      } else {
        setMessage({
          text: msg,
          color: "text-red-600",
          icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
        });
      }
    } catch (error) {
      const msg =
        error.response?.data?.msg || "Bilet onaylanırken bir hata oluştu.";
      setMessage({
        text: msg,
        color: "text-red-600",
        icon: <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />,
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center py-4 px-4 mt-6 font-sans text-gray-900">
      {/* Üst Bar */}
      <div className="w-full max-w-lg flex justify-between items-center mb-6 bg-gray-50 p-4 rounded-2xl shadow-sm border border-gray-300">
        <div className="flex items-center gap-3">
          <div className="bg-brand/10 p-2 rounded-xl">
            <ScanLine className="w-6 h-6 text-brand" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 tracking-tight">
            Bilet Kontrol
          </h1>
        </div>
        <button
          onClick={handleLogout}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Ana Alan */}
      <div className="w-full max-w-lg flex-1 flex flex-col gap-4">
        {/* Kamera Kontrol Butonları */}
        {!isCameraOpen ? (
          <button
            onClick={startScanner}
            className="w-full bg-brand text-white rounded-2xl py-6 shadow-lg shadow-brand/60 active:scale-[0.98] transition-all flex flex-col items-center justify-center gap-3 hover:bg-brand/90"
          >
            <Camera className="w-12 h-12" />
            <span className="text-lg font-semibold">Taramayı Başlat</span>
          </button>
        ) : (
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-black aspect-square w-full max-w-sm mx-auto">
            <Scanner
              onScan={handleScan}
              onError={handleError}
              constraints={{ facingMode: "environment" }}
              components={{
                audio: false,
                finder: false, // Kendi finder'ımızı yapabiliriz veya bunu true yapabiliriz
              }}
              styles={{
                container: { width: "100%", height: "100%" },
                video: { objectFit: "cover" },
              }}
            />
            {/* Özel Vizör */}
            <div className="absolute inset-0 border-2 border-white/30 rounded-3xl pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-brand rounded-3xl shadow-[0_0_0_9999px_rgba(0,0,0,0.7)]"></div>
            </div>

            <button
              onClick={stopScanner}
              className="absolute top-4 right-4 bg-black/50 text-white p-3 rounded-full backdrop-blur-sm hover:bg-black/70 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Loading Overlay */}
        {isScanning && (
          <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm text-white animate-in fade-in duration-200">
            <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-lg font-medium">Bilet Bilgisi Getiriliyor...</p>
          </div>
        )}

        {/* Bilgi Kartı (Kamera Kapalıyken ve Loading değilken) */}
        {!isCameraOpen && !isScanning && (
          <div className="bg-gray-50 p-6 rounded-3xl shadow-sm border border-gray-200 text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <ScanLine className="w-8 h-8 text-gray-600" />
            </div>
            <p className="text-gray-600 font-semibold">
              Kamerayı başlatmak için yukarıdaki butona dokunun.
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg text-gray-800">Bilet Detayı</h2>
              <button
                onClick={closeModal}
                className="p-1 bg-white rounded-full shadow-sm border text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {message ? (
                <div className="text-center py-4">
                  {message.icon}
                  <h3 className={`text-xl font-bold ${message.color} mb-2`}>
                    {message.text}
                  </h3>
                </div>
              ) : (
                ticketData && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-2xl space-y-2">
                      <div>
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                          Etkinlik
                        </span>
                        <p className="font-semibold text-gray-900">
                          {ticketData?.etkinlik?.title || "-"}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            Ad Soyad
                          </span>
                          <p className="font-medium text-gray-900">
                            {ticketData?.name || "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            TC No
                          </span>
                          <p className="font-medium text-gray-900">
                            {ticketData?.tc || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-200/50 mt-2">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                          Bilet Kodu
                        </span>
                        <p className="font-mono text-lg font-bold text-brand tracking-widest">
                          {ticketData?.code || "-"}
                        </p>
                      </div>
                      <div className="pt-2 border-t border-gray-200/50 mt-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                              Seans
                            </span>
                            <p className="font-mono text-lg font-bold text-brand tracking-widest">
                              {ticketData?.seans || "-"}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                              Koltuk Bilgileri
                            </span>
                            <div className="flex items-center gap-2">
                              <p className="font-mono text-lg font-bold text-brand tracking-widest">
                                {ticketData?.koltuk_sira || "-"} -{" "}
                                {ticketData?.koltuk_numara || "-"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Aksiyon Butonları */}
              {showButtons && (
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <button
                    onClick={cancelTicket}
                    disabled={processing}
                    className="flex items-center justify-center gap-2 bg-red-50 text-red-600 border-2 border-red-100 hover:bg-red-100 py-3.5 rounded-xl font-bold transition-colors disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5" />
                    {processing ? "..." : "İptal Et"}
                  </button>
                  <button
                    onClick={confirmTicket}
                    disabled={processing}
                    className="flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700 py-3.5 rounded-xl font-bold shadow-lg shadow-green-600/20 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {processing ? "..." : "Onayla"}
                  </button>
                </div>
              )}

              {/* Devam Et Butonu (Mesaj varsa) */}
              {message && (
                <button
                  onClick={closeModal}
                  className="w-full mt-6 bg-gray-900 text-white py-3.5 rounded-xl font-bold hover:bg-gray-800 transition-colors shadow-lg"
                >
                  Devam Et
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
